import jsPDF from "jspdf"
import "jspdf-autotable"
import { deliverPdf } from "./helpers/pdfDelivery.js"

/**
 * Genera un PDF de recibo de pago de cuota
 * @param {Object} cuota - Datos de la cuota pagada
 * @param {Object} transaction - Datos de la transacción
 * @param {Object} cuentaCorriente - Datos de la cuenta corriente
 * @param {Object} cuotasInfo - Información del plan de cuotas
 * @param {Boolean} returnBase64 - Si es true, retorna base64; si es false, descarga el PDF
 * @param {Boolean} showFechaViaje - Si es true, muestra la fecha de viaje en el recibo
 * @returns {String|void} - Retorna base64 si returnBase64=true, o undefined si solo descarga
 */
export const generateReceiptPDF = (cuota, transaction, cuentaCorriente, cuotasInfo, returnBase64 = false, logoBase64 = null, deliveryContext = null, showFechaViaje = true) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" })
  
  // Paleta de colores
  const PAL = {
    primary: [25, 118, 210],
    secondary: [120, 144, 156],
    success: [76, 175, 80],
    warning: [255, 152, 0],
    error: [244, 67, 54],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250]
  }
  
  const M = { l: 48, r: 48, t: 56, b: 56, gridGap: 14 }
  
  // Helpers UI
  const text = (str, x, y, { size = 10, color = PAL.ink, align = "left", bold = false } = {}) => {
    doc.setFont("helvetica", bold ? "bold" : "normal")
    doc.setFontSize(size)
    doc.setTextColor(...color)
    doc.text(str, x, y, { align })
  }
  
  const badge = (label, x, y, color = PAL.primary) => {
    doc.setFont("helvetica", "bold")
    doc.setFontSize(9)
    const padX = 8, padY = 4
    const w = doc.getTextWidth(label) + padX * 2
    const h = 16
    doc.setFillColor(...color)
    doc.roundedRect(x, y - h + 2, w, h, 8, 8, "F")
    doc.setTextColor(255, 255, 255)
    doc.text(label, x + padX, y - 5)
    doc.setTextColor(...PAL.ink)
  }
  
  const sectionTitle = (label, y) => {
    doc.setDrawColor(...PAL.primary)
    doc.setLineWidth(1)
    doc.line(M.l, y + 6, doc.internal.pageSize.getWidth() - M.r, y + 6)
    text(label, M.l, y, { size: 12, color: PAL.primary, bold: true })
  }
  
  const kvRow = (label, value) => ([
    { content: label, styles: { halign: "left", fontStyle: "bold", textColor: PAL.muted } },
    { content: value ?? "N/A", styles: { halign: "left" } }
  ])
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(value)
  }
  
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const dateObj = new Date(dateString)
    if (isNaN(dateObj.getTime())) return String(dateString)
    return new Intl.DateTimeFormat("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(dateObj)
  }

  const getImageFormat = (dataUrl) => {
    const match = /^data:image\/(png|jpe?g|webp);/i.exec(dataUrl || "")
    if (!match) return "PNG"
    const ext = match[1].toLowerCase()
    if (ext === "jpg" || ext === "jpeg") return "JPEG"
    if (ext === "webp") return "WEBP"
    return "PNG"
  }
  
  // HEADER
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  
  // Logo (logoTenant)
  if (logoBase64) {
    try {
      const logoMaxW = 90
      const logoMaxH = 42
      const props = doc.getImageProperties(logoBase64)
      const ratio = Math.min(logoMaxW / props.width, logoMaxH / props.height)
      const logoW = props.width * ratio
      const logoH = props.height * ratio
      const logoX = M.l
      const logoY = 14
      doc.addImage(logoBase64, getImageFormat(logoBase64), logoX, logoY, logoW, logoH)
    } catch (error) {
      // Ignorar errores de logo para no bloquear el PDF
    }
  }
  
  text("RECIBO DE PAGO", pageWidth / 2, 38, {
    size: 16, color: PAL.primary, align: "center", bold: true
  })
  
  text(`Fecha de emisión: ${new Date().toLocaleDateString("es-AR")}`, pageWidth - M.r, 58, {
    size: 10, color: PAL.inkLight, align: "right"
  })
  
  const numRecibo = `Recibo Nº ${String(cuota.cuotaNro).padStart(4, "0")}`
  text(numRecibo, pageWidth - M.r, 84, { size: 10, color: PAL.muted, align: "right", bold: true })
  
  // DATOS DEL CLIENTE + INFORMACIÓN DEL VIAJE (en paralelo)
  let cursorY = 120
  sectionTitle("DATOS DEL CLIENTE", cursorY)
  const columnWidth = (pageWidth - M.l - M.r - M.gridGap) / 2
  const rightColX = M.l + columnWidth + M.gridGap
  text("INFORMACIÓN DEL VIAJE", rightColX, cursorY, { size: 12, color: PAL.primary, bold: true })
  doc.setDrawColor(...PAL.primary)
  doc.setLineWidth(1)
  doc.line(rightColX, cursorY + 6, pageWidth - M.r, cursorY + 6)
  cursorY += 24
  
  const nombreCompleto = `${cuentaCorriente?.entidadCompleta?.descripcion || "N/A"} ${cuentaCorriente?.entidadCompleta?.apellido || ""}`.trim()
  const dniCuit = cuentaCorriente?.entidadCompleta?.dniCuitCuil || "N/A"
  const telefono = cuentaCorriente?.entidadCompleta?.telefono || cuentaCorriente?.entidadCompleta?.celular || "N/A"
  const fechaViaje = cuotasInfo?.paquete?.fechaInicio || cuotasInfo?.fechaInicio || cuotasInfo?.fechaViaje || null
  
  doc.autoTable({
    startY: cursorY,
    margin: { left: M.l },
    tableWidth: columnWidth,
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 3, textColor: PAL.ink },
    columnStyles: { 0: { cellWidth: 140 } },
    body: [
      kvRow("Cliente", nombreCompleto),
      kvRow("DNI/CUIT/CUIL", dniCuit),
      kvRow("Teléfono", telefono)
    ]
  })
  
  const clienteFinalY = doc.lastAutoTable.finalY
  
  doc.autoTable({
    startY: cursorY,
    margin: { left: rightColX },
    tableWidth: columnWidth,
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 3, textColor: PAL.ink },
    columnStyles: { 0: { cellWidth: 140 } },
    body: [
      kvRow("Grupo", cuotasInfo?.item?.descripcion || "N/A"),
      kvRow("Destino", cuotasInfo?.ubicacion?.descripcion || "N/A"),
      ...(showFechaViaje ? [kvRow("Fecha de Viaje", formatDate(fechaViaje))] : [])
    ]
  })
  
  const viajeFinalY = doc.lastAutoTable.finalY
  cursorY = Math.max(clienteFinalY, viajeFinalY) + 24
  
  // INFORMACIÓN DEL PAGO
  sectionTitle("INFORMACIÓN DEL PAGO", cursorY)
  cursorY += 24

  const totalCuotas = (cuotasInfo?.pagadas ?? 0) + (cuotasInfo?.pendientes ?? 0)
  const cuotaNroLabel = totalCuotas > 0 ? `${cuota.cuotaNro}/${totalCuotas}` : String(cuota.cuotaNro)
  const saldoAPagar = Number(cuotasInfo?.totalPendiente ?? 0) || 0

  doc.autoTable({
    startY: cursorY,
    margin: { left: M.l, right: M.r },
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 3, textColor: PAL.ink },
    columnStyles: { 0: { cellWidth: 140 } },
    body: [
      kvRow("Cuota Nº", cuotaNroLabel),
      //kvRow("ID Transacción", String(cuota.idTransaccion || "N/A")),
      kvRow("Fecha de Pago", formatDate(cuota.transaccion?.fecha || new Date())),
      kvRow("Saldo a pagar", formatCurrency(saldoAPagar))
    ]
  })
  
  cursorY = doc.lastAutoTable.finalY + 28
  
  // MONTO PAGADO (desde idPago/Pago; fallback a cuota)
  const importePagado = Number(cuota.montoPagado ?? cuota.monto ?? cuota.montoBase ?? 0) || 0
  
  const montoBoxHeight = 65
  const montoBoxWidth = 280
  const montoBoxX = (pageWidth - montoBoxWidth) / 2
  
  doc.setFillColor(...PAL.bgSoft)
  doc.roundedRect(montoBoxX, cursorY, montoBoxWidth, montoBoxHeight, 4, 4, "F")
  doc.setDrawColor(...PAL.success)
  doc.setLineWidth(2)
  doc.roundedRect(montoBoxX, cursorY, montoBoxWidth, montoBoxHeight, 4, 4)
  
  text("MONTO PAGADO", pageWidth / 2, cursorY + 22, {
    size: 10, color: PAL.muted, align: "center", bold: true
  })
  
  text(formatCurrency(importePagado), pageWidth / 2, cursorY + 48, {
    size: 22, color: PAL.success, align: "center", bold: true
  })
  
  cursorY += montoBoxHeight + 28
  
  // FOOTER
  const footerY = pageHeight - 90
  
  doc.setDrawColor(...PAL.inkLight)
  doc.setLineWidth(0.5)
  doc.line(M.l, footerY, pageWidth - M.r, footerY)
  
  /*text("Este recibo es válido como comprobante de pago", pageWidth / 2, footerY + 25, {
    size: 8, color: PAL.inkLight, align: "center"
  })*/
  
  text("Documento informativo · No válido como comprobante fiscal", pageWidth / 2, pageHeight - M.b + 10, {
    size: 7, color: PAL.inkLight, align: "center"
  })
  
  // METADATA
  doc.setProperties({
    title: `Recibo_Cuota_${cuota.cuotaNro}`,
    subject: "Recibo de Pago de Cuota",
    author: "Agencia de Viajes",
    creator: "Sistema de Gestión"
  })
  
  const fileName = `recibo_cuota${cuota.cuotaNro}_${dniCuit}_${new Date().toISOString().slice(0,10)}.pdf`
  
  // ✅ Retornar base64 o descargar
  if (returnBase64) {
    return doc.output("datauristring") // "data:application/pdf;base64,..."
  } else {
    deliverPdf({ doc, fileName, targetWindow: deliveryContext?.targetWindow || null })
  }
}

/**
 * Genera un PDF de recibo de devolución de pago de cuota
 * @param {Object} cuota - Datos de la cuota revertida
 * @param {Object} datosReversion - Datos de la reversión (fechaPagoOriginal, montoRevertido, etc.)
 * @param {Object} cuentaCorriente - Datos de la cuenta corriente
 * @param {Object} cuotasInfo - Información del plan de cuotas
 * @param {Boolean} returnBase64 - Si es true, retorna base64; si es false, descarga el PDF
 * @param {Boolean} showFechaViaje - Si es true, muestra la fecha de viaje en el recibo
 * @returns {String|void} - Retorna base64 si returnBase64=true, o undefined si solo descarga
 */
export const generateRefundReceiptPDF = (cuota, datosReversion, cuentaCorriente, cuotasInfo, returnBase64 = false, logoBase64 = null, deliveryContext = null, showFechaViaje = true) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" })
  
  // Paleta de colores
  const PAL = {
    primary: [25, 118, 210],
    secondary: [120, 144, 156],
    success: [76, 175, 80],
    warning: [255, 152, 0],
    error: [244, 67, 54],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250]
  }
  
  const M = { l: 48, r: 48, t: 56, b: 56, gridGap: 14 }
  
  // Helpers UI
  const text = (str, x, y, { size = 10, color = PAL.ink, align = "left", bold = false } = {}) => {
    doc.setFont("helvetica", bold ? "bold" : "normal")
    doc.setFontSize(size)
    doc.setTextColor(...color)
    doc.text(str, x, y, { align })
  }
  
  const badge = (label, x, y, color = PAL.primary) => {
    doc.setFont("helvetica", "bold")
    doc.setFontSize(9)
    const padX = 8, padY = 4
    const w = doc.getTextWidth(label) + padX * 2
    const h = 16
    doc.setFillColor(...color)
    doc.roundedRect(x, y - h + 2, w, h, 8, 8, "F")
    doc.setTextColor(255, 255, 255)
    doc.text(label, x + padX, y - 5)
    doc.setTextColor(...PAL.ink)
  }
  
  const sectionTitle = (label, y) => {
    doc.setDrawColor(...PAL.primary)
    doc.setLineWidth(1)
    doc.line(M.l, y + 6, doc.internal.pageSize.getWidth() - M.r, y + 6)
    text(label, M.l, y, { size: 12, color: PAL.primary, bold: true })
  }
  
  const kvRow = (label, value) => ([
    { content: label, styles: { halign: "left", fontStyle: "bold", textColor: PAL.muted } },
    { content: value ?? "N/A", styles: { halign: "left" } }
  ])
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(value)
  }
  
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const dateObj = new Date(dateString)
    if (isNaN(dateObj.getTime())) return String(dateString)
    return new Intl.DateTimeFormat("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(dateObj)
  }

  const getImageFormat = (dataUrl) => {
    const match = /^data:image\/(png|jpe?g|webp);/i.exec(dataUrl || "")
    if (!match) return "PNG"
    const ext = match[1].toLowerCase()
    if (ext === "jpg" || ext === "jpeg") return "JPEG"
    if (ext === "webp") return "WEBP"
    return "PNG"
  }
  
  // HEADER
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  
  // Logo (logoTenant)
  if (logoBase64) {
    try {
      const logoMaxW = 90
      const logoMaxH = 42
      const props = doc.getImageProperties(logoBase64)
      const ratio = Math.min(logoMaxW / props.width, logoMaxH / props.height)
      const logoW = props.width * ratio
      const logoH = props.height * ratio
      const logoX = M.l
      const logoY = 14
      doc.addImage(logoBase64, getImageFormat(logoBase64), logoX, logoY, logoW, logoH)
    } catch (error) {
      // Ignorar errores de logo para no bloquear el PDF
    }
  }
  
  text("RECIBO DE DEVOLUCIÓN", pageWidth / 2, 38, {
    size: 16, color: PAL.error, align: "center", bold: true
  })
  
  text(`Fecha de emisión: ${new Date().toLocaleDateString("es-AR")}`, pageWidth - M.r, 58, {
    size: 10, color: PAL.inkLight, align: "right"
  })
  
  const numRecibo = `Devolución Cuota Nº ${String(cuota.cuotaNro).padStart(4, "0")}`
  text(numRecibo, pageWidth - M.r, 84, { size: 10, color: PAL.muted, align: "right", bold: true })
  
  // DATOS DEL CLIENTE + INFORMACIÓN DEL VIAJE (en paralelo)
  let cursorY = 120
  sectionTitle("DATOS DEL CLIENTE", cursorY)
  const columnWidth = (pageWidth - M.l - M.r - M.gridGap) / 2
  const rightColX = M.l + columnWidth + M.gridGap
  text("INFORMACIÓN DEL VIAJE", rightColX, cursorY, { size: 12, color: PAL.primary, bold: true })
  doc.setDrawColor(...PAL.primary)
  doc.setLineWidth(1)
  doc.line(rightColX, cursorY + 6, pageWidth - M.r, cursorY + 6)
  cursorY += 24
  
  const nombreCompleto = `${cuentaCorriente?.entidadCompleta?.descripcion || "N/A"} ${cuentaCorriente?.entidadCompleta?.apellido || ""}`.trim()
  const dniCuit = cuentaCorriente?.entidadCompleta?.dniCuitCuil || "N/A"
  const telefono = cuentaCorriente?.entidadCompleta?.telefono || cuentaCorriente?.entidadCompleta?.celular || "N/A"
  const fechaViaje = cuotasInfo?.paquete?.fechaInicio || cuotasInfo?.fechaInicio || cuotasInfo?.fechaViaje || null
  
  doc.autoTable({
    startY: cursorY,
    margin: { left: M.l },
    tableWidth: columnWidth,
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 3, textColor: PAL.ink },
    columnStyles: { 0: { cellWidth: 140 } },
    body: [
      kvRow("Cliente", nombreCompleto),
      kvRow("DNI/CUIT/CUIL", dniCuit),
      kvRow("Teléfono", telefono)
    ]
  })
  
  const clienteFinalY = doc.lastAutoTable.finalY
  
  doc.autoTable({
    startY: cursorY,
    margin: { left: rightColX },
    tableWidth: columnWidth,
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 3, textColor: PAL.ink },
    columnStyles: { 0: { cellWidth: 140 } },
    body: [
      kvRow("Grupo", cuotasInfo?.item?.descripcion || "N/A"),
      kvRow("Destino", cuotasInfo?.ubicacion?.descripcion || "N/A"),
      ...(showFechaViaje ? [kvRow("Fecha de Viaje", formatDate(fechaViaje))] : [])
    ]
  })
  
  const viajeFinalY = doc.lastAutoTable.finalY
  cursorY = Math.max(clienteFinalY, viajeFinalY) + 24
  
  // INFORMACIÓN DE LA REVERSIÓN
  sectionTitle("INFORMACIÓN DE LA REVERSIÓN", cursorY)
  cursorY += 24

  const totalCuotas = (cuotasInfo?.pagadas ?? 0) + (cuotasInfo?.pendientes ?? 0)
  const cuotaNroLabel = totalCuotas > 0 ? `${cuota.cuotaNro}/${totalCuotas}` : String(cuota.cuotaNro)
  const saldoAPagar = Number(cuotasInfo?.totalPendiente ?? 0) || 0

  doc.autoTable({
    startY: cursorY,
    margin: { left: M.l, right: M.r },
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 3, textColor: PAL.ink },
    columnStyles: { 0: { cellWidth: 140 } },
    body: [
      kvRow("Cuota Nº", cuotaNroLabel),
      //kvRow("ID Transacción Original", String(datosReversion?.idTransaccionRevertida || "N/A")),
      kvRow("Fecha de Pago Original", formatDate(datosReversion?.fechaPagoOriginal)),
      kvRow("Fecha de Reversión", formatDate(datosReversion?.fechaReversion || new Date())),
      kvRow("Nuevo Saldo a Pagar", formatCurrency(saldoAPagar))
    ]
  })
  
  cursorY = doc.lastAutoTable.finalY + 28
  
  // MONTO REVERTIDO (en rojo/naranja)
  const montoRevertido = Number(datosReversion?.montoRevertido ?? cuota.monto ?? cuota.montoBase ?? 0) || 0
  
  const montoBoxHeight = 65
  const montoBoxWidth = 280
  const montoBoxX = (pageWidth - montoBoxWidth) / 2
  
  doc.setFillColor(...PAL.bgSoft)
  doc.roundedRect(montoBoxX, cursorY, montoBoxWidth, montoBoxHeight, 4, 4, "F")
  doc.setDrawColor(...PAL.error)
  doc.setLineWidth(2)
  doc.roundedRect(montoBoxX, cursorY, montoBoxWidth, montoBoxHeight, 4, 4)
  
  text("MONTO REVERTIDO", pageWidth / 2, cursorY + 22, {
    size: 10, color: PAL.muted, align: "center", bold: true
  })
  
  text(formatCurrency(montoRevertido), pageWidth / 2, cursorY + 48, {
    size: 22, color: PAL.error, align: "center", bold: true
  })
  
  cursorY += montoBoxHeight + 28
  
  // MENSAJE DE ADVERTENCIA
  const warningBoxHeight = 50
  const warningBoxWidth = 450
  const warningBoxX = (pageWidth - warningBoxWidth) / 2
  
  doc.setFillColor(255, 243, 224) // Fondo amarillo claro
  doc.roundedRect(warningBoxX, cursorY, warningBoxWidth, warningBoxHeight, 4, 4, "F")
  doc.setDrawColor(...PAL.warning)
  doc.setLineWidth(1)
  doc.roundedRect(warningBoxX, cursorY, warningBoxWidth, warningBoxHeight, 4, 4)
  
  text("El pago de esta cuota ha sido cancelado.", pageWidth / 2, cursorY + 20, {
    size: 10, color: PAL.ink, align: "center", bold: true
  })
  
  text("La cuota vuelve a estado pendiente y puede ser pagada nuevamente.", pageWidth / 2, cursorY + 38, {
    size: 9, color: PAL.muted, align: "center"
  })
  
  cursorY += warningBoxHeight + 28
  
  // FOOTER
  const footerY = pageHeight - 90
  
  doc.setDrawColor(...PAL.inkLight)
  doc.setLineWidth(0.5)
  doc.line(M.l, footerY, pageWidth - M.r, footerY)
  
  text("Documento informativo · No válido como comprobante fiscal", pageWidth / 2, pageHeight - M.b + 10, {
    size: 7, color: PAL.inkLight, align: "center"
  })
  
  // METADATA
  doc.setProperties({
    title: `Devolucion_Cuota_${cuota.cuotaNro}`,
    subject: "Recibo de Devolución de Pago de Cuota",
    author: "Agencia de Viajes",
    creator: "Sistema de Gestión"
  })
  
  const fileName = `devolucion_cuota${cuota.cuotaNro}_${dniCuit}_${new Date().toISOString().slice(0,10)}.pdf`
  
  // ✅ Retornar base64 o descargar
  if (returnBase64) {
    return doc.output("datauristring") // "data:application/pdf;base64,..."
  } else {
    deliverPdf({ doc, fileName, targetWindow: deliveryContext?.targetWindow || null })
  }
}
